import { useLocation, Outlet } from "react-router-dom"
import { useTranslation } from "react-i18next"
import toastr from "toastr"

import ProgressBar from "components/progress-bar"
import { Header } from "components/header"
import { NotFound } from "components/not-found"
import { EventDetails } from "components/event-details"
import { AppPlaceholder } from "components/app-placeholder"
import { AdminCallout } from "components/admin-callout"
import { NoPublishedTickets } from "components/no-published-tickets"
import { Timer } from "pages/ticket-details/timer"
import { useBaseContext } from "contexts/use-base-context"
import { useRegistrationData } from "hooks/api/details/use-registration-data"
import { useEventData } from "hooks/api/details/use-event-data"
import { useAuthentication } from "hooks/navigation/use-authentication"
import { useNavigationEffect } from "hooks/navigation/use-navigation-effect"
import { useThemeAndCurrency } from "hooks/ui/use-theme-and-currency"
import { useMetricsTracking } from "hooks/ui/use-metrics-tracking"
import { useDeviceId } from "hooks/utils/use-device-id"
import { isPreview } from "utils/constants"

// Configure toastr globally
toastr.options = {
  positionClass: "toast-bottom-left",
  escapeHtml: true,
}

const App = () => {
  const { t } = useTranslation()
  document.title = t("general.document_title")

  const location = useLocation()
  const { isAdmin } = useBaseContext()

  // Custom hooks for clean separation of concerns
  const {
    registrationId,
    bundleUUID,
    guideId,
    isLive,
    isLoading: isRegistrationDataLoading,
  } = useRegistrationData()

  const {
    data: eventData,
    isLoading: isEventDataLoading,
  } = useEventData(bundleUUID)

  // Initialize all side effects through custom hooks
  useAuthentication()
  useDeviceId()
  useNavigationEffect()
  useThemeAndCurrency(eventData?.results?.[0], isLive)
  useMetricsTracking(registrationId, guideId)

  // Computed values
  const showTimer = location.pathname.includes("ticket-details")

  // Early returns for loading and error states
  if (isRegistrationDataLoading || isEventDataLoading) {
    return <AppPlaceholder />
  }

  if (!isLive && isPreview) {
    return <NoPublishedTickets />
  }

  if (!isLive) {
    return <NotFound />
  }

  // Main app layout
  return (
    <div>
      <Header />
      {isAdmin && <AdminCallout />}
      <ProgressBar />
      {showTimer && <Timer />}
      <EventDetails eventData={eventData?.results[0]} />
      <Outlet />
    </div>
  )
}

export { App }